import { isValidElement } from 'react';

export type Perf = {
  (mark: string, measure?: string[]): void;
  (mark: string, children: React.ReactNode): React.ReactNode;
  (
    mark: string,
    mountMeasure: string[],
    children: React.ReactNode
  ): React.ReactNode;
  (
    mark: string,
    mountMeasure: string[],
    unmountMeasure: string[],
    children: React.ReactNode
  ): React.ReactNode;
};

const isClient = (() => {
  try {
    return window instanceof Window;
  } catch (e) {
    return false;
  }
})();

/**
 * the perf() function makes marks and measures on the performance timeline. It's a thin,
 * JSX-aware wrapper over the performance API.
 *
 * Does not do anything unless NODE_ENV is development.
 *
 * ```typescript
 * // make a mark
 * perf('frobnicate:start')
 *
 * // mark and measure previous marks
 * perf('frobnicate:end', ['frobnicate:start'])
 *
 * // return JSX which will produce marks and measures when it is mounted:
 * perf('settings',         // mark settings:mount and settings:unmount
 *   ['frobnicate:start'],  // on mount, measure from frobnicate:start
 *   ['settings:mount',
 *    'frobnicate:end'],    // on unmount, measure from settings:mount
 *                          // and frobnicate:end
 *   <Settings />)          // render this JSX
 *
 */
export const perf: Perf =
  isClient && process.env.NODE_ENV === 'development'
    ? (function perfImpl(mark: string, ...rest: any[]): ReturnType<Perf> {
        if (rest.length === 0) {
          performance.mark(mark);
          return undefined;
        }
        let arg = rest.shift();
        const measures: string[][] = [];
        while (Array.isArray(arg)) {
          measures.push(arg);
          arg = rest.shift();
        }
        if (isValidElement(arg)) {
          const [mountMeasure = [], unmountMeasure = []] = measures;
          return (
            <>
              {/* @ts-ignore */}
              <vapor-performance-marks
                mount-mark={`${mark}:mount`}
                mount-measure={mountMeasure.join(' ')}
                unmount-mark={`${mark}:unmount`}
                unmount-measure={unmountMeasure.join(' ')}
              />
              {arg}
            </>
          );
        }
        // Handle case where we have measures but no JSX
        for (const measureArray of measures) {
          for (const startMark of measureArray) {
            const measure = `${startMark} to ${mark}`;
            if (performance.getEntriesByName(startMark).length > 0) {
              performance.measure(measure, startMark, mark);
            }
          }
        }
        return arg;
      } as Perf)
    : (function noopPerf(...args: any[]) {
        return args[args.length - 1];
      } as Perf);

if (isClient && process.env.NODE_ENV === 'development') {
  let logPerformanceEvents = false;
  let observer: PerformanceObserver | null = null;
  Object.defineProperty(window.performance, '_vapor_logPerformanceEvents', {
    get() {
      return logPerformanceEvents;
    },
    set(value) {
      logPerformanceEvents = value;
      if (logPerformanceEvents && !observer) {
        // eslint-disable-next-line no-console
        console.log('Logging performance events');
        observer = new PerformanceObserver(printEvents);
        observer.observe({ entryTypes: ['mark', 'measure'] });
      }
      if (!logPerformanceEvents && observer) {
        observer.disconnect();
        observer = null;
        // eslint-disable-next-line no-console
        console.log('Stopped logging performance events');
      }
    },
  });

  // eslint-disable-next-line no-unused-vars, no-inner-declarations
  function printEvents(list: PerformanceObserverEntryList) {
    list.getEntries().forEach((entry) => {
      // eslint-disable-next-line no-console
      console.log(entry);
    });
  }

  try {
    class VaporPerformanceMarks extends HTMLElement {
      connectedCallback() {
        const tag = this.getAttribute('mount-mark');
        if (tag) {
          performance.mark(tag);
        }
        const since = this.getAttribute('mount-measure');
        if (since && tag) {
          for (const startTag of since.split(/\s+/)) {
            const measure = `${startTag} to ${tag}`;
            if (performance.getEntriesByName(startTag).length === 0) {
              // eslint-disable-next-line no-continue
              continue;
            }
            performance.measure(measure, startTag, tag);
          }
        }
      }

      disconnectedCallback() {
        const tag = this.getAttribute('unmount-mark');
        if (tag) {
          performance.mark(tag);
        }
        const since = this.getAttribute('unmount-measure');
        if (since && tag) {
          for (const startTag of since.split(/\s+/)) {
            const measure = `${startTag} to ${tag}`;
            if (performance.getEntriesByName(startTag).length === 0) {
              // eslint-disable-next-line no-continue
              continue;
            }
            performance.measure(measure, startTag, tag);
          }
        }
      }
    }

    customElements.define('vapor-performance-marks', VaporPerformanceMarks);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error defining vapor-performance-marks');
    // eslint-disable-next-line no-console
    console.error(e);
  }
}
